import React from "react";
import { Platform, Pressable, StyleSheet, Text, View } from "react-native";

type Props = {
  onBack: () => void;
  onNext: () => void;
  nextDisabled?: boolean;
  nextLabel?: string;
  variant?: "dark" | "light";
  maxWidth?: number;
  center?: boolean;
  backVariant?: "ghost" | "secondary";

  /**
   * Use "compact" for desktop/web layouts where vertical space is limited.
   * Keep "default" for mobile so buttons remain easy to tap.
   */
  size?: "default" | "compact";
};

export function FooterNav({
  onBack,
  onNext,
  nextDisabled = false,
  nextLabel = "Next: Review ->",
  variant = "dark",
  maxWidth,
  center,
  backVariant = "ghost",
  size = "default",
}: Props) {
  const isLight = variant === "light";
  const isCompact = size === "compact";

  const backButtonStyle =
    backVariant === "secondary"
      ? styles.btnBackSecondary
      : isLight
      ? styles.btnGhostLight
      : styles.btnGhostDark;

  const backTextStyle =
    backVariant === "secondary"
      ? styles.textLight
      : isLight
      ? styles.textDark
      : styles.textLight;

  return (
    <View
      style={[
        styles.wrap,
        center && styles.center,
        typeof maxWidth === "number" ? ({ maxWidth } as any) : null,
      ]}
    >
      <View style={[styles.row, isCompact && styles.rowCompact]}>
        <Pressable
          onPress={onBack}
          style={[
            styles.btn,
            isCompact && styles.btnCompact,
            backButtonStyle,
          ]}
        >
          <Text
            style={[
              styles.btnText,
              isCompact && styles.btnTextCompact,
              backTextStyle,
            ]}
          >
            {"<- Back"}
          </Text>
        </Pressable>

        <Pressable
          onPress={onNext}
          disabled={nextDisabled}
          style={[
            styles.btn,
            isCompact && styles.btnCompact,
            nextDisabled ? styles.btnDisabled : styles.btnPrimary,
          ]}
        >
          <Text
            style={[
              styles.btnText,
              isCompact && styles.btnTextCompact,
              nextDisabled ? styles.textDisabled : styles.textLight,
            ]}
          >
            {nextLabel}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    width: "100%",
  },

  center: {
    alignSelf: "center",
  },

  row: {
    flexDirection: "row",
    gap: 12,
  },

  rowCompact: {
    gap: 10,
  },

  btn: {
    flex: 1,
    minHeight: 52,
    paddingHorizontal: 14,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },

  btnCompact: {
    minHeight: 44,
    paddingHorizontal: 12,
    borderRadius: 14,
  },

  btnGhostDark: {
    backgroundColor: "rgba(255,255,255,0.06)",
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.10)",
  },

  btnGhostLight: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    ...(Platform.OS === "web"
      ? ({ boxShadow: "0 4px 12px rgba(0,0,0,0.04)" } as any)
      : null),
  },

  btnBackSecondary: {
    backgroundColor: "#111827",
    borderWidth: 1,
    borderColor: "rgba(17,24,39,0.18)",
    ...(Platform.OS === "web"
      ? ({ boxShadow: "0 4px 12px rgba(0,0,0,0.08)" } as any)
      : null),
  },

  btnPrimary: {
    backgroundColor: "#2563EB",
  },

  btnDisabled: {
    backgroundColor: "#BFDBFE",
    borderWidth: 1,
    borderColor: "#93C5FD",
  },

  btnText: {
    fontWeight: "800",
    fontSize: 15,
  },

  btnTextCompact: {
    fontSize: 14,
  },

  textLight: {
    color: "#FFFFFF",
  },

  textDark: {
    color: "#0F172A",
  },

  textDisabled: {
    color: "#475569",
  },
});
